import { filterUtils } from './filterUtils'

export interface LUTData {
  size: number
  data: Float32Array
  title?: string
}

type CanvasContext =
  | CanvasRenderingContext2D
  | OffscreenCanvasRenderingContext2D

function sampleLUT(
  data: Float32Array,
  size: number,
  r: number,
  g: number,
  b: number
): { r: number; g: number; b: number } {
  const maxIdx = size - 1
  const rS = Math.max(0, Math.min(1, r)) * maxIdx
  const gS = Math.max(0, Math.min(1, g)) * maxIdx
  const bS = Math.max(0, Math.min(1, b)) * maxIdx

  const r0 = Math.floor(rS)
  const g0 = Math.floor(gS)
  const b0 = Math.floor(bS)
  const r1 = Math.min(r0 + 1, maxIdx)
  const g1 = Math.min(g0 + 1, maxIdx)
  const b1 = Math.min(b0 + 1, maxIdx)

  const fr = rS - r0
  const fg = gS - g0
  const fb = bS - b0

  const idx = (ri: number, gi: number, bi: number): number =>
    (ri + gi * size + bi * size * size) * 3

  const sample = (ri: number, gi: number, bi: number): { r: number; g: number; b: number } => {
    const i = idx(ri, gi, bi)
    return { r: data[i], g: data[i + 1], b: data[i + 2] }
  }

  const c000 = sample(r0, g0, b0)
  const c100 = sample(r1, g0, b0)
  const c010 = sample(r0, g1, b0)
  const c110 = sample(r1, g1, b0)
  const c001 = sample(r0, g0, b1)
  const c101 = sample(r1, g0, b1)
  const c011 = sample(r0, g1, b1)
  const c111 = sample(r1, g1, b1)

  const lerp = (
    a: { r: number; g: number; b: number },
    b: { r: number; g: number; b: number },
    t: number
  ): { r: number; g: number; b: number } => ({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t
  })

  const c00 = lerp(c000, c100, fr)
  const c10 = lerp(c010, c110, fr)
  const c01 = lerp(c001, c101, fr)
  const c11 = lerp(c011, c111, fr)
  const c0 = lerp(c00, c10, fg)
  const c1 = lerp(c01, c11, fg)
  return lerp(c0, c1, fb)
}

export function applyLUT(ctx: CanvasContext, lutData: LUTData): void {
  const width = ctx.canvas.width
  const height = ctx.canvas.height
  if (width === 0 || height === 0) return

  const imageData = ctx.getImageData(0, 0, width, height)
  const pixels = imageData.data
  const { data, size } = lutData

  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i] / 255
    const g = pixels[i + 1] / 255
    const b = pixels[i + 2] / 255
    const out = sampleLUT(data, size, r, g, b)
    pixels[i] = Math.round(Math.max(0, Math.min(255, out.r * 255)))
    pixels[i + 1] = Math.round(Math.max(0, Math.min(255, out.g * 255)))
    pixels[i + 2] = Math.round(Math.max(0, Math.min(255, out.b * 255)))
  }

  ctx.putImageData(imageData, 0, 0)
}

const lutCache = new Map<string, LUTData>()

export async function fetchLUTData(filterId: string): Promise<LUTData | null> {
  const cached = lutCache.get(filterId)
  if (cached) return cached

  const base = process.env.VITE_API_URL || ''
  const url = `${base}/api/filters/lut?filterId=${filterId}`
  try {
    const res = await fetch(url, { credentials: 'include', cache: 'no-cache' })
    if (!res.ok) return null
    const json = await res.json()
    const lutData: LUTData = {
      size: json.size,
      data: new Float32Array(json.data),
      title: json.title
    }
    lutCache.set(filterId, lutData)
    return lutData
  } catch {
    return null
  }
}

export function getCachedLUT(filterId: string): LUTData | null {
  return lutCache.get(filterId) ?? null
}

export function clearLUTCache(): void {
  lutCache.clear()
}

/** Filter shape needed for applyLUTThenOverlay (avoids full Filter import) */
export interface FilterForLUT {
  _id: string
  filterMode?: 'manual' | 'lut'
  postLUTAdjustments?: { vignette?: number; grain?: number; blur?: number; clarity?: number; sharpeningAmount?: number }
}

/**
 * Apply LUT (when present) then postLUTAdjustments overlay. Use for LUT-mode filters only.
 * Does not use or apply manual adjustments; those stay for Manual mode.
 */
export function applyLUTThenOverlay(
  ctx: CanvasContext,
  filter: FilterForLUT
): void {
  const lutData = getCachedLUT(filter._id)
  if (lutData) {
    applyLUT(ctx, lutData)
  }
  const overlay = filter.postLUTAdjustments
  if (overlay && Object.keys(overlay).length > 0) {
    filterUtils.applyAdjustments(ctx, overlay)
  }
}
